/*
 * When something happened, as the web app says it: in the viewer's own time zone, in British
 * English, and never to the second. A row or a Timeline entry says it relatively ("5m ago"), and
 * its tooltip says it in full ("29 September 2026, 14:03"). Spend's days are UTC and keep their own
 * wording in `spend/describe.ts`.
 */

/** A moment as the web API sends it, or as the web app already holds it. */
export type Moment = string | number | Date

const minute = 60 * 1000
const hour = 60 * minute

const dayMonth = new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "short" })
const dayMonthYear = new Intl.DateTimeFormat("en-GB", {
  day: "numeric",
  month: "short",
  year: "numeric",
})
const longDate = new Intl.DateTimeFormat("en-GB", { dateStyle: "long" })
const clock = new Intl.DateTimeFormat("en-GB", {
  hour: "2-digit",
  minute: "2-digit",
  hourCycle: "h23",
})

const dateOf = (moment: Moment) => (moment instanceof Date ? moment : new Date(moment))

/** Midnight at the start of a moment's day, in the viewer's time zone. */
const dayStart = (date: Date) => new Date(date.getFullYear(), date.getMonth(), date.getDate())

/**
 * How long ago a moment was, for a row or a Timeline entry: "just now" under a minute, then minutes
 * and hours on the same day, "Yesterday" for the day before, then the date — with its year only
 * when that is not this one. A moment from a clock running ahead of this one is "just now".
 */
export function describeRelativeTime(moment: Moment, now: Date = new Date()) {
  const at = dateOf(moment)
  const ago = now.getTime() - at.getTime()
  if (ago < minute) return "just now"
  if (ago < hour) return `${Math.floor(ago / minute)}m ago`
  const today = dayStart(now)
  if (at >= today) return `${Math.floor(ago / hour)}h ago`
  const yesterday = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 1)
  if (at >= yesterday) return "Yesterday"
  return at.getFullYear() === now.getFullYear() ? dayMonth.format(at) : dayMonthYear.format(at)
}

/**
 * A moment in full, for the tooltip over a relative one: "29 September 2026, 14:03". Composed from
 * two formats, since en-GB's own joins them with "at".
 */
export function describeAbsoluteTime(moment: Moment) {
  const at = dateOf(moment)
  return `${longDate.format(at)}, ${clock.format(at)}`
}

/** A moment's day in full: "28 September 2026". */
export function describeLongDate(moment: Moment) {
  return longDate.format(dateOf(moment))
}
